/**
 * Master Data Helper Utilities
 * マスタデータ関連のユーティリティ関数群
 */

/**
 * マスタタイプから表示名を取得
 */
export function getMasterDisplayName(masterType) {
  const displayNames = {
    event: '種目マスタ',
    race_type: 'イベント格式マスタ',
    event_race_type: '種目とイベント格式マスタ',
    race_owner: '主催者マスタ',
    class_owner: '種別マスタ',
    hall: '会場マスタ',
    mfj_hall: '会場マスタ',
    race_series: '大会シリーズマスタ',
    displace: '排気量マスタ',
    race_owner_access: '主催者アクセスマスタ',
    race_owner_event: '主催者種目マスタ'
  }

  return displayNames[masterType] || masterType
}

/**
 * アイテムのマスタタイプを推定
 */
export function inferMasterType(item) {
  if (item.race_series_id) return 'race_series'
  if (item.event_race_id || (item.event_cd && item.race_type_cd))
    return 'event_race_type'
  if (item.race_type_cd && !item.event_cd) return 'race_type'
  if (item.race_owner_access_id) return 'race_owner_access'
  if (item.race_owner_event_id) return 'race_owner_event'
  if (item.race_owner_login_id && !item.race_owner_access_id)
    return 'race_owner'
  if (item.class_owner_cd) return 'class_owner'
  if (item.mfj_hall_hall_cd) return 'hall'
  if (item.displace_id) return 'displace'
  if (item.event_cd) return 'event'

  return null
}

/**
 * マスタタイプから主キー名を取得
 */
export function getPrimaryKeyName(masterType) {
  const primaryKeys = {
    event: 'event_id',
    race_type: 'race_type_id',
    event_race_type: 'event_race_id',
    race_owner: 'race_owner_id',
    race_owner_event: 'race_owner_event_id',
    race_owner_access: 'race_owner_access_id',
    class_owner: 'class_owner_id',
    hall: 'mfj_hall_id',
    race_series: 'race_series_id',
    displace: 'displace_id'
  }

  return primaryKeys[masterType] || 'id'
}

/**
 * アイテムから適切なIDを取得
 */
export function getItemId(item, masterType = null) {
  const type = masterType || inferMasterType(item)
  if (!type) return null

  const primaryKey = getPrimaryKeyName(type)
  return item[primaryKey]
}

/**
 * 日付フォーマット（YYYYMMDD → YYYY-MM-DD）
 */
export function formatYmdDate(val) {
  // null, undefined, 空文字の場合はそのまま返す
  if (!val) {
    return val
  }

  // 数値の場合は文字列に変換
  if (typeof val === 'number') {
    val = val.toString()
  }

  if (typeof val === 'string') {
    // 8桁（YYYYMMDD）→ YYYY-MM-DD
    if (/^\d{8}$/.test(val)) {
      return `${val.substr(0, 4)}-${val.substr(4, 2)}-${val.substr(6, 2)}`
    }
    // 6桁（YYYYMM）→ YYYY-MM
    if (/^\d{6}$/.test(val)) {
      return `${val.substr(0, 4)}-${val.substr(4, 2)}`
    }
    // 4桁（YYYY）→ そのまま返す（年のみ）
    if (/^\d{4}$/.test(val)) {
      return val
    }
    // 既にYYYY-MM-DD形式の場合はそのまま返す
    if (/^\d{4}-\d{2}-\d{2}$/.test(val)) {
      return val
    }
    // 既にYYYY-MM形式の場合はそのまま返す
    if (/^\d{4}-\d{2}$/.test(val)) {
      return val
    }
  }
  return val
}

/**
 * 日付を YYYYMMDD 形式に変換（バックエンド送信用）
 */
export function convertDateToYYYYMMDD(val) {
  if (!val) {
    return null
  }

  // YYYY-MM-DD 形式の場合
  if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(val)) {
    return val.replace(/-/g, '')
  }

  // YYYY-MM 形式の場合（月初に設定）
  if (typeof val === 'string' && /^\d{4}-\d{2}$/.test(val)) {
    return val.replace(/-/g, '') + '01'
  }

  // 既に8桁の場合はそのまま返す
  if (typeof val === 'string' && /^\d{8}$/.test(val)) {
    return val
  }

  return val
}

/**
 * アコーディオンタイトル生成
 */
export function getAccordionTitle(item, masterTypes = []) {
  // グループデータの場合
  if (item._group_type && item._title) {
    return item._title
  }

  // マスタタイプから表示名を取得
  const masterType = item._master_type
  if (masterType) {
    const masterTypeOption = masterTypes.find(type => type.value === masterType)
    return masterTypeOption
      ? masterTypeOption.label
      : getMasterDisplayName(masterType)
  }

  return '不明なマスタタイプ'
}

/**
 * マスタタイプのラベルからバリューを取得
 */
export function getMasterTypeValue(label, masterTypes = []) {
  const masterTypeMap = {
    種目マスタ: 'event',
    イベント格式マスタ: 'race_type',
    種目とイベント格式マスタ: 'event_race_type',
    主催者マスタ: 'race_owner',
    主催者種目マスタ: 'race_owner_event',
    主催者アクセスマスタ: 'race_owner_access',
    種別マスタ: 'class_owner',
    会場マスタ: 'hall',
    大会シリーズマスタ: 'race_series',
    排気量マスタ: 'displace'
  }

  // 直接マッピングから取得
  if (masterTypeMap[label]) {
    return masterTypeMap[label]
  }

  // masterTypesから検索
  const masterTypeObj = masterTypes.find(type => type.label === label)
  return masterTypeObj?.value || null
}

/**
 * ページネーション用のページ番号配列生成
 */
export function generatePageNumbers(totalPages, maxVisible = 10) {
  if (totalPages <= maxVisible) {
    return Array.from({ length: totalPages }, (_, i) => i + 1)
  }

  // より複雑なページネーションロジックが必要な場合はここに実装
  return Array.from({ length: totalPages }, (_, i) => i + 1)
}

/**
 * カラムマップ定義（表示用）
 */
export const COLUMN_MAPS = {
  event: [
    { key: 'event_cd', label: '種目コード' },
    { key: 'event_name', label: '種目名' },
    { key: 'event_short_name', label: '種目略称' },
    { key: 'event_view_number', label: '表示番号' },
    { key: 'event_limit_startdate', label: '開催開始日' },
    { key: 'event_limit_enddate', label: '開催終了日' }
  ],
  race_type: [
    { key: 'race_type_cate', label: '格式コード' },
    { key: 'race_type_cate_name', label: '格式名' },
    { key: 'race_type_cd', label: '格式コード' },
    { key: 'race_type_name', label: '格式名' },
    { key: 'race_numer', label: '格式番号' },
    { key: 'point_flg', label: 'ポイントフラグ' }
  ],
  race_owner: [
    { key: 'race_owner_login_id', label: '主催者コード' },
    { key: 'race_owner_name', label: '主催者名' },
    { key: 'race_owner_name_kana', label: '主催者名カナ' },
    { key: 'race_chairman_name', label: '代表者氏名' }
  ],
  hall: [
    { key: 'mfj_hall_hall_cd', label: '会場コード' },
    { key: 'mfj_hall_hall_name', label: '会場名' },
    { key: 'mfj_hall_hall_kana', label: '会場名カナ' },
    { key: 'mfj_hall_chairman', label: '代表者氏名' }
  ],
  // 主催者種目マスタ
  race_owner_event: [
    { key: 'race_owner_event_id', label: '主催者種目ID' },
    { key: 'event_cd', label: '種目コード' },
    { key: 'event_name', label: '種目名' },
    { key: 'race_owner_login_id', label: '主催者ログインID' },
    { key: 'race_owner_cd', label: '主催者コード' }
  ],
  // 主催者アクセスマスタ
  race_owner_access: [
    { key: 'race_owner_access_id', label: 'アクセスID' },
    { key: 'race_owner_login_id', label: '主催者ログインID' },
    { key: 'race_owner_access_seq', label: '連絡先番号' },
    { key: 'race_owner_access_name', label: '連絡先名称' },
    { key: 'race_owner_access_name_kana', label: '連絡先名称カナ' },
    { key: 'race_owner_access_tel', label: '電話番号' }
  ],
  // 種別マスタ
  class_owner: [
    { key: 'class_owner_id', label: '種別ID' },
    { key: 'class_owner_kbn_cd', label: '種別区分コード' },
    { key: 'class_owner_kbn_name', label: '種別区分名' },
    { key: 'class_owner_cd', label: '種別コード' },
    { key: 'class_owner_name', label: '種別名' },
    { key: 'class_limit_startdate', label: '有効開始日' },
    { key: 'class_limit_enddate', label: '有効終了日' }
  ],
  // 大会シリーズマスタ
  race_series: [
    { key: 'race_series_id', label: 'シリーズID' },
    { key: 'event_cd', label: '種目コード' },
    { key: 'race_type_cd', label: '格式コード' },
    { key: 'race_series_cd', label: 'シリーズコード' },
    { key: 'race_series_name', label: 'シリーズ名' },
    { key: 'view_number', label: '表示番号' },
    { key: 'race_series_limit_startdate', label: '開始日' },
    { key: 'race_series_limit_enddate', label: '終了日' }
  ],
  // 排気量マスタ
  displace: [
    { key: 'displace_id', label: '排気量ID' },
    { key: 'event_cd', label: '種目コード' },
    { key: 'displace_cd', label: '排気量コード' },
    { key: 'displace_name', label: '排気量名' },
    { key: 'displace_short_name', label: '排気量略称' },
    { key: 'view_number', label: '表示番号' }
  ],
  // 種目とイベント格式マスタ
  event_race_type: [
    { key: 'event_race_id', label: 'イベントレースID' },
    { key: 'event_cd', label: '種目コード' },
    { key: 'race_type_cd', label: '格式コード' },
    { key: 'race_type_name', label: '格式名' },
    { key: 'race_sports_year', label: '競技年' },
    { key: 'tournament_application_name', label: '大会申請名' },
    { key: 'tournament_application_money', label: '申請金額' }
  ]
}

/**
 * マスタタイプに応じたカラム定義を取得
 */
export function getItemColumns(item, fallbackMasterType = null) {
  const masterType = item._master_type || fallbackMasterType
  return COLUMN_MAPS[masterType] || []
}

/**
 * マスタ作成フォーム用フィールド定義
 */
export const getMasterFormFields = masterType => {
  const fieldDefinitions = {
    // 1. 種目マスタ
    event: [
      {
        name: 'event_cd',
        label: '種目コード',
        type: 'varchar',
        required: true,
        placeholder: '例: MX',
        help: '2-10文字の英数字で入力してください'
      },
      {
        name: 'event_name',
        label: '種目名',
        type: 'varchar',
        required: true,
        placeholder: '例: モトクロス',
        help: '100文字以内で入力してください'
      },
      {
        name: 'event_short_name',
        label: '種目略称',
        type: 'varchar',
        required: true,
        placeholder: '例: MX',
        help: '20文字以内で入力してください'
      },
      {
        name: 'event_view_number',
        label: '表示番号',
        type: 'decimal',
        required: false,
        placeholder: '例: 1',
        help: '表示順序を数値で指定してください',
        default: 99
      },
      {
        name: 'event_limit_startdate',
        label: '開催開始日',
        type: 'date',
        required: false,
        placeholder: '例: 2024-04-01',
        help: 'YYYY-MM-DD形式で入力してください'
      },
      {
        name: 'event_limit_enddate',
        label: '開催終了日',
        type: 'date',
        required: false,
        placeholder: '例: 2025-03-31',
        help: 'YYYY-MM-DD形式で入力してください'
      }
    ],

    // 2. レースタイプマスタ
    race_type: [
      {
        name: 'race_type_cate',
        label: 'レースタイプカテゴリ',
        type: 'varchar',
        required: false,
        placeholder: '例: 01'
      },
      {
        name: 'race_type_cate_name',
        label: 'レースタイプカテゴリ名',
        type: 'varchar',
        required: false,
        placeholder: '例: 全日本選手権'
      },
      {
        name: 'world_race_social_cd',
        label: '世界レース社会コード',
        type: 'varchar',
        required: false,
        placeholder: '例: WR001'
      },
      {
        name: 'world_race_social_name',
        label: '世界レース社会名',
        type: 'varchar',
        required: false,
        placeholder: '例: 世界モータースポーツ協会'
      },
      {
        name: 'tournament_cd',
        label: '大会コード',
        type: 'varchar',
        required: false,
        placeholder: '例: T001'
      },
      {
        name: 'tournament_name',
        label: '大会名',
        type: 'varchar',
        required: false,
        placeholder: '例: 全日本選手権大会'
      },
      {
        name: 'race_type_cd',
        label: 'レースタイプコード',
        type: 'varchar',
        required: true,
        placeholder: '例: JMX'
      },
      {
        name: 'race_type_name',
        label: 'レースタイプ名',
        type: 'varchar',
        required: true,
        placeholder: '例: 全日本モトクロス選手権'
      },
      {
        name: 'race_numer',
        label: 'レース番号',
        type: 'integer',
        required: false,
        placeholder: '例: 1'
      },
      {
        name: 'point_flg',
        label: 'ポイントフラグ',
        type: 'varchar',
        required: false,
        placeholder: '例: 1',
        help: '1:ポイント対象、0:対象外'
      },
      {
        name: 'race_limit_startdate',
        label: 'レース開始日',
        type: 'integer',
        required: false,
        placeholder: '例: 20240401',
        help: 'YYYYMMDD形式で入力してください'
      },
      {
        name: 'race_limit_enddate',
        label: 'レース終了日',
        type: 'integer',
        required: false,
        placeholder: '例: 20241231',
        help: 'YYYYMMDD形式で入力してください'
      }
    ],

    // 3. 種目とイベント格式マスタ
    event_race_type: [
      {
        name: 'event_cd',
        label: '種目コード',
        type: 'varchar',
        required: true,
        placeholder: '例: MX',
        help: '種目コードを入力してください'
      },
      {
        name: 'race_type_cd',
        label: 'レースタイプコード',
        type: 'varchar',
        required: true,
        placeholder: '例: JMX',
        help: 'レースタイプコードを入力してください'
      },
      {
        name: 'race_type_name',
        label: 'レースタイプ名',
        type: 'varchar',
        required: false,
        placeholder: '例: 全日本モトクロス選手権',
        help: 'レースタイプ名を入力してください'
      },
      {
        name: 'race_sports_year',
        label: '競技年',
        type: 'integer',
        required: true,
        placeholder: '例: 2024',
        help: '競技年を4桁で入力してください'
      },
      {
        name: 'tournament_application_name',
        label: '大会申請名',
        type: 'varchar',
        required: false,
        placeholder: '例: 全日本モトクロス選手権大会',
        help: '大会申請名を入力してください'
      },
      {
        name: 'tournament_application_money',
        label: '申請金額',
        type: 'integer',
        required: false,
        placeholder: '例: 50000',
        help: '申請金額を円単位で入力してください'
      },
      {
        name: 'subject_cd',
        label: '科目コード',
        type: 'varchar',
        required: false,
        placeholder: '例: S001'
      },
      {
        name: 'subject_cd_nmber',
        label: '科目コード番号',
        type: 'varchar',
        required: false,
        placeholder: '例: 01'
      },
      {
        name: 'cusomer_insurance_name',
        label: '顧客保険名',
        type: 'varchar',
        required: false,
        placeholder: '例: 競技者保険'
      },
      {
        name: 'race_insurance',
        label: 'レース保険',
        type: 'integer',
        required: false,
        placeholder: '例: 1',
        help: '1:加入済み、0:未加入'
      },
      {
        name: 'subject_cd2',
        label: '科目コード2',
        type: 'varchar',
        required: false,
        placeholder: '例: S002'
      },
      {
        name: 'subject_cd2_nmber',
        label: '科目コード2番号',
        type: 'varchar',
        required: false,
        placeholder: '例: 02'
      }
    ],

    // 4. 主催者マスタ
    race_owner: [
      {
        name: 'race_owner_login_id',
        label: '主催者ログインID',
        type: 'varchar',
        required: true,
        placeholder: '例: owner001',
        help: '英数字とアンダースコアで入力してください'
      },
      {
        name: 'race_owner_name',
        label: '主催者名',
        type: 'varchar',
        required: true,
        placeholder: '例: 株式会社モータースポーツ'
      },
      {
        name: 'race_owner_name_kana',
        label: '主催者名カナ',
        type: 'varchar',
        required: false,
        placeholder: '例: カブシキガイシャモータースポーツ'
      },
      {
        name: 'race_chairman_name',
        label: '代表者氏名',
        type: 'varchar',
        required: false,
        placeholder: '例: 田中太郎'
      },
      {
        name: 'race_owner_bill_flg',
        label: '請求書フラグ',
        type: 'varchar',
        required: false,
        placeholder: '例: 1',
        help: '1:請求書発行、0:発行しない'
      },
      {
        name: 'race_owner_race_kbn',
        label: '個人団体区分',
        type: 'varchar',
        required: false,
        placeholder: '例: 1',
        help: '1:個人、2:団体'
      },
      {
        name: 'class_owner_cd',
        label: '主催者種別コード',
        type: 'varchar',
        required: false,
        placeholder: '例: C001'
      },
      {
        name: 'race_owner_state_region_cd',
        label: '地区コード',
        type: 'varchar',
        required: false,
        placeholder: '例: 01'
      },
      {
        name: 'race_owner_state_cd',
        label: '都道府県コード',
        type: 'varchar',
        required: false,
        placeholder: '例: 13'
      },
      {
        name: 'race_owner_post_cd',
        label: '郵便番号',
        type: 'varchar',
        required: false,
        placeholder: '例: 123-4567'
      },
      {
        name: 'race_owner_address1',
        label: '住所1',
        type: 'varchar',
        required: false,
        placeholder: '例: 東京都渋谷区道玄坂'
      },
      {
        name: 'race_owner_address2',
        label: '住所2',
        type: 'varchar',
        required: false,
        placeholder: '例: 1-1-1'
      },
      {
        name: 'race_owner_tel',
        label: '電話番号',
        type: 'varchar',
        required: false,
        placeholder: '例: 03-1234-5678'
      },
      {
        name: 'race_owner_cellphone',
        label: '携帯電話',
        type: 'varchar',
        required: false,
        placeholder: '例: 090-1234-5678'
      },
      {
        name: 'race_owner_fax',
        label: 'FAX番号',
        type: 'varchar',
        required: false,
        placeholder: '例: 03-1234-5679'
      },
      {
        name: 'race_owner_mail',
        label: 'メールアドレス',
        type: 'varchar',
        required: false,
        placeholder: '例: owner@example.com'
      }
    ],

    // 5. 主催者種目マスタ
    race_owner_event: [
      {
        name: 'event_cd',
        label: '種目コード',
        type: 'varchar',
        required: true,
        placeholder: '例: MX',
        help: '種目コードを入力してください'
      },
      {
        name: 'event_name',
        label: '種目名',
        type: 'varchar',
        required: false,
        placeholder: '例: モトクロス',
        help: '種目名を入力してください'
      },
      {
        name: 'race_owner_login_id',
        label: '主催者ログインID',
        type: 'varchar',
        required: true,
        placeholder: '例: 00000001',
        help: '主催者ログインIDを入力してください'
      },
      {
        name: 'race_owner_cd',
        label: '主催者コード',
        type: 'varchar',
        required: false,
        placeholder: '例: 00000001',
        help: '主催者コードを入力してください'
      }
    ],

    // 6. 主催者アクセスマスタ
    race_owner_access: [
      {
        name: 'race_owner_login_id',
        label: '主催者ログインID',
        type: 'varchar',
        required: true,
        placeholder: '例: 00000001',
        help: '主催者ログインIDを入力してください'
      },
      {
        name: 'race_owner_access_seq',
        label: '連絡先番号',
        type: 'varchar',
        required: false,
        placeholder: '例: 01',
        help: '連絡先番号を入力してください'
      },
      {
        name: 'race_owner_access_name',
        label: '連絡先名称',
        type: 'varchar',
        required: true,
        placeholder: '例: 管理者連絡先',
        help: '連絡先名称を入力してください'
      },
      {
        name: 'race_owner_access_name_kana',
        label: '連絡先名称カナ',
        type: 'varchar',
        required: false,
        placeholder: '例: カンリシャレンラクサキ',
        help: '連絡先名称のカナを入力してください'
      },
      {
        name: 'race_owner_access_post_cd',
        label: '連絡先郵便番号',
        type: 'varchar',
        required: false,
        placeholder: '例: 123-4567'
      },
      {
        name: 'race_owner_access_address1',
        label: '連絡先住所1',
        type: 'varchar',
        required: false,
        placeholder: '例: 東京都渋谷区'
      },
      {
        name: 'race_owner_access_address2',
        label: '連絡先住所2',
        type: 'varchar',
        required: false,
        placeholder: '例: 1-1-1'
      },
      {
        name: 'race_owner_access_tel',
        label: '電話番号',
        type: 'varchar',
        required: false,
        placeholder: '例: 03-1234-5678',
        help: '電話番号を入力してください'
      },
      {
        name: 'race_owner_access_cellphone',
        label: '携帯電話番号',
        type: 'varchar',
        required: false,
        placeholder: '例: 090-1234-5678'
      },
      {
        name: 'race_owner_access_fax',
        label: 'FAX番号',
        type: 'varchar',
        required: false,
        placeholder: '例: 03-1234-5679'
      }
    ],

    // 7. 種別マスタ
    class_owner: [
      {
        name: 'class_owner_kbn_cd',
        label: '種別区分コード',
        type: 'varchar',
        required: true,
        placeholder: '例: 01'
      },
      {
        name: 'class_owner_kbn_name',
        label: '種別区分名',
        type: 'varchar',
        required: true,
        placeholder: '例: 排気量別'
      },
      {
        name: 'class_owner_cd',
        label: '種別コード',
        type: 'varchar',
        required: true,
        placeholder: '例: IA1'
      },
      {
        name: 'class_owner_name',
        label: '種別名',
        type: 'varchar',
        required: true,
        placeholder: '例: IA1クラス'
      },
      {
        name: 'class_limit_startdate',
        label: '有効開始日',
        type: 'integer',
        required: false,
        placeholder: '例: 20240401',
        help: 'YYYYMMDD形式で入力してください'
      },
      {
        name: 'class_limit_enddate',
        label: '有効終了日',
        type: 'integer',
        required: false,
        placeholder: '例: 20250331',
        help: 'YYYYMMDD形式で入力してください'
      }
    ],

    // 8. 会場マスタ
    hall: [
      {
        name: 'mfj_hall_hall_cd',
        label: '会場コード',
        type: 'varchar',
        required: true,
        placeholder: '例: H001'
      },
      {
        name: 'mfj_hall_hall_name',
        label: '会場名',
        type: 'varchar',
        required: true,
        placeholder: '例: 鈴鹿サーキット'
      },
      {
        name: 'mfj_hall_hall_kana',
        label: '会場名カナ',
        type: 'varchar',
        required: true,
        placeholder: '例: スズカサーキット',
        help: '必須項目です'
      },
      {
        name: 'mfj_hall_chairman',
        label: '責任者',
        type: 'varchar',
        required: false,
        placeholder: '例: 山田花子'
      },
      {
        name: 'mfj_hall_circuit',
        label: 'サーキット名',
        type: 'varchar',
        required: false,
        placeholder: '例: 鈴鹿サーキット国際レーシングコース'
      },
      {
        name: 'mfj_hall_data_todoufuken',
        label: '都道府県コード',
        type: 'varchar',
        required: true,
        placeholder: '例: 24',
        help: '必須項目です'
      },
      {
        name: 'mfj_hall_areacd',
        label: '地区コード',
        type: 'varchar',
        required: true,
        placeholder: '例: 03',
        help: '必須項目です'
      },
      {
        name: 'mfj_hall_data_zip_cd',
        label: '郵便番号',
        type: 'varchar',
        required: true,
        placeholder: '例: 510-0295',
        help: '必須項目です'
      },
      {
        name: 'mfj_hall_data_address1',
        label: '住所1',
        type: 'varchar',
        required: true,
        placeholder: '例: 三重県鈴鹿市稲生町',
        help: '必須項目です'
      },
      {
        name: 'mfj_hall_data_address2',
        label: '住所2',
        type: 'varchar',
        required: false,
        placeholder: '例: 7992'
      },
      {
        name: 'mfj_hall_tel',
        label: '電話番号',
        type: 'varchar',
        required: false,
        placeholder: '例: 059-378-1111'
      },
      {
        name: 'mfj_hall_cellphone',
        label: '携帯電話番号',
        type: 'varchar',
        required: false,
        placeholder: '例: 090-1234-5678'
      },
      {
        name: 'mfj_hall_fax',
        label: 'FAX番号',
        type: 'varchar',
        required: false,
        placeholder: '例: 059-378-1112'
      },
      {
        name: 'mfj_hall_email',
        label: 'メールアドレス',
        type: 'varchar',
        required: false,
        placeholder: '例: info@suzukacircuit.jp'
      },
      {
        name: 'mfj_hall_callname',
        label: '連絡先名称',
        type: 'varchar',
        required: false,
        placeholder: '例: 鈴鹿サーキット事務局'
      },
      {
        name: 'mfj_hall_callname_kana',
        label: '連絡先名称フリガナ',
        type: 'varchar',
        required: false,
        placeholder: '例: スズカサーキットジムキョク'
      },
      {
        name: 'mfj_hall_call_zip_cd',
        label: '連絡先郵便番号',
        type: 'varchar',
        required: false,
        placeholder: '例: 510-0295'
      },
      {
        name: 'mfj_hall_call_address1',
        label: '連絡先住所1',
        type: 'varchar',
        required: false,
        placeholder: '例: 三重県鈴鹿市稲生町'
      },
      {
        name: 'mfj_hall_call_address2',
        label: '連絡先住所2',
        type: 'varchar',
        required: false,
        placeholder: '例: 7992'
      },
      {
        name: 'mfj_hall_call_tel',
        label: '連絡先電話番号',
        type: 'varchar',
        required: false,
        placeholder: '例: 059-378-1111'
      },
      {
        name: 'mfj_hall_call_cellphone',
        label: '連絡先携帯電話番号',
        type: 'varchar',
        required: false,
        placeholder: '例: 090-1234-5678'
      },
      {
        name: 'mfj_hall_call_fax',
        label: '連絡先FAX番号',
        type: 'varchar',
        required: false,
        placeholder: '例: 059-378-1112'
      },
      {
        name: 'mfj_hall_last_riyou_year',
        label: '最終使用年',
        type: 'integer',
        required: false,
        placeholder: '例: 2024'
      },
      {
        name: 'mfj_hall_shinsei_date',
        label: '新規申請日',
        type: 'date',
        required: false,
        placeholder: '例: 2024-01-01'
      },
      {
        name: 'mfj_hall_bikou',
        label: '備考',
        type: 'varchar',
        required: false,
        placeholder: '例: 特記事項'
      }
    ],

    // 9. 大会シリーズマスタ
    race_series: [
      {
        name: 'event_cd',
        label: '種目コード',
        type: 'varchar',
        required: true,
        placeholder: '例: MX'
      },
      {
        name: 'race_type_cd',
        label: '格式コード',
        type: 'varchar',
        required: true,
        placeholder: '例: JMX'
      },
      {
        name: 'race_series_cd',
        label: 'シリーズコード',
        type: 'varchar',
        required: true,
        placeholder: '例: JMX2024'
      },
      {
        name: 'race_series_name',
        label: 'シリーズ名',
        type: 'varchar',
        required: true,
        placeholder: '例: 全日本モトクロス選手権2024'
      },
      {
        name: 'view_number',
        label: '表示番号',
        type: 'integer',
        required: false,
        placeholder: '例: 1',
        default: 99
      },
      {
        name: 'race_series_limit_startdate',
        label: '開始日',
        type: 'integer',
        required: false,
        placeholder: '例: 20240401',
        help: 'YYYYMMDD形式で入力してください'
      },
      {
        name: 'race_series_limit_enddate',
        label: '終了日',
        type: 'integer',
        required: false,
        placeholder: '例: 20241231',
        help: 'YYYYMMDD形式で入力してください'
      }
    ],

    // 10. 排気量マスタ
    displace: [
      {
        name: 'event_cd',
        label: '種目コード',
        type: 'varchar',
        required: true,
        placeholder: '例: MX',
        help: '種目コードを入力してください'
      },
      {
        name: 'displace_cd',
        label: '排気量コード',
        type: 'varchar',
        required: true,
        placeholder: '例: 250'
      },
      {
        name: 'displace_name',
        label: '排気量名',
        type: 'varchar',
        required: true,
        placeholder: '例: 250cc'
      },
      {
        name: 'displace_short_name',
        label: '排気量略称',
        type: 'varchar',
        required: false,
        placeholder: '例: 250'
      },
      {
        name: 'view_number',
        label: '表示番号',
        type: 'integer',
        required: false,
        placeholder: '例: 1',
        help: '表示順序を数値で指定してください'
      },
      {
        name: 'race_series_limit_startdate',
        label: '開始日',
        type: 'integer',
        required: false,
        placeholder: '例: 20240401',
        help: 'YYYYMMDD形式で入力してください'
      },
      {
        name: 'race_series_limit_enddate',
        label: '終了日',
        type: 'integer',
        required: false,
        placeholder: '例: 20241231',
        help: 'YYYYMMDD形式で入力してください'
      }
    ]
  }

  return fieldDefinitions[masterType] || []
}
